import useTenantState from '@/states/tenant.state';
import { env } from '@/utils/constants/env';
import { getKeycloakInstance, loginWithKeycloak, logoutFromKeycloak, getAccessToken } from './keycloak-instance';

export interface KeycloakTokenResponse {
  access_token: string;
  refresh_token: string;
  token_type: string;
  expires_in: number;
}

export interface KeycloakUserInfo {
  sub: string;
  email: string; 
  given_name?: string;
  family_name?: string;
  preferred_username?: string;
  name?: string;
  // Additional Keycloak standard claims
  email_verified?: boolean;
  locale?: string;
  picture?: string;
  // Custom claims that may be added by Keycloak admin
  tenant_role?: string;
  trial_ends_at?: string | null;
  avatar_url?: string | null;
}

/**
 * KeycloakAuthService now wraps keycloak-js library
 * This maintains backward compatibility while using the official client
 */
export class KeycloakAuthService {
  private static instance: KeycloakAuthService;

  private constructor() { }

  public static getInstance(): KeycloakAuthService {
    if (!KeycloakAuthService.instance) {
      KeycloakAuthService.instance = new KeycloakAuthService();
    }
    return KeycloakAuthService.instance;
  }

  /**
   * Login using keycloak-js
   * This replaces the manual PKCE flow
   */
  public async login(redirectUri?: string): Promise<void> {
    await loginWithKeycloak(redirectUri);
  }

  /**
   * Exchange authorization code for tokens (deprecated)
   * keycloak-js handles this internally
   */
  public async exchangeCodeForTokens(
    code: string,
    codeVerifier: string
  ): Promise<KeycloakTokenResponse> {
    console.warn('exchangeCodeForTokens is deprecated - keycloak-js handles this automatically');
    
    const tokenEndpoint = `${env.keycloakUrl}/realms/${env.keycloakRealm}/protocol/openid-connect/token`;

    const body = new URLSearchParams({
      grant_type: 'authorization_code',
      client_id: env.keycloakClientId,
      client_secret: env.keycloakClientSecret || '',
      code,
      redirect_uri: env.keycloakRedirectUri,
      code_verifier: codeVerifier,
    });

    const response = await fetch(tokenEndpoint, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
      },
      body: body.toString(),
    });

    if (!response.ok) {
      const errorData = await response.text();
      throw new Error(`Token exchange failed: ${response.status} - ${errorData}`);
    }

    return response.json();
  }

  /**
   * Refresh access token using keycloak-js
   */
  public async refreshAccessToken(refreshToken?: string): Promise<KeycloakTokenResponse> {
    const keycloak = getKeycloakInstance();
    
    try {
      const refreshed = await keycloak.updateToken(-1); // Force refresh
      
      if (refreshed && keycloak.token && keycloak.refreshToken) {
        return {
          access_token: keycloak.token,
          refresh_token: keycloak.refreshToken,
          token_type: 'Bearer',
          expires_in: keycloak.tokenParsed?.exp || 0,
        };
      } else {
        throw new Error('Token refresh failed');
      }
    } catch (error) {
      console.error('Failed to refresh token:', error);
      throw new Error(`Token refresh failed: ${error}`);
    }
  }

  /**
   * Get user information from backend API
   */
  public async getUserInfo(accessToken: string): Promise<KeycloakUserInfo> {
    const userInfoEndpoint = `${env.apiTasklistBaseUrl}/api/v1/authentication/profile`;

    const response = await fetch(userInfoEndpoint, {
      headers: {
        Authorization: `Bearer ${accessToken}`,
        'x-active-tenant': String(useTenantState.getState().currentTenant?.slug ?? ''),
      },
    });

    if (!response.ok) {
      const errorData = await response.text();
      throw new Error(`Failed to get user info: ${response.status} - ${errorData}`);
    }

    const user = await response.json();
    return user.data;
  }

  /**
   * Logout using keycloak-js
   */
  public async logout(
    refreshToken?: string,
    redirectUri: string = globalThis.location.origin + '/login'
  ): Promise<void> {
    await logoutFromKeycloak(redirectUri);
  }

  /**
   * Validate and decode JWT token
   */
  public decodeToken(token: string): any {
    try {
      const tokenParts = token.split('.');
      if (tokenParts.length !== 3) {
        throw new Error('Invalid token format');
      }

      const payload = JSON.parse(atob(tokenParts[1]));
      return payload;
    } catch (error) {
      console.error('Error decoding token:', error);
      throw error;
    }
  }

  /**
   * Check if token is expired
   */
  public isTokenExpired(token: string): boolean {
    try {
      const payload = this.decodeToken(token);
      const exp = payload.exp;

      if (typeof exp !== 'number') {
        return true;
      }

      // Add a small buffer (30 seconds) to prevent edge cases
      return (exp * 1000) < (Date.now() + 30000);
    } catch (error) {
      console.warn('Error checking token expiration:', error);
      return true;
    }
  }

  /**
   * Get current access token from keycloak-js instance
   */
  public async getValidToken(): Promise<string | undefined> {
    return await getAccessToken();
  }
}

export const keycloakAuth = KeycloakAuthService.getInstance();
